import React, { useState, useEffect, useRef } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import AutoLinks from 'quill-auto-links';
import { linkify } from 'util/stringUtils';
import { toJS } from 'mobx';
import { observer, inject } from 'mobx-react';
import classnames from 'classnames';
import _ from 'lodash';
import { ReactComponent as RightArrowIcon } from 'assets/images/icons/svgs/right-arrow.svg';
import { ReactComponent as NonProfitIcon } from 'assets/images/icons/svgs/non-profit.svg';
import { ReactComponent as PencilIcon } from 'assets/images/icons/svgs/pencil-cauze.svg';
import Button from 'components/Button/Button';
import DatePicker from 'components/DatePicker';
import CharitySearch from 'components/CharitySearch/CharitySearch';
import ImageUploadModal from 'components/modals/ImageUploadModal/ImageUploadModal';
import CauzeSpinner from 'components/CauzeSpinner/CauzeSpinner';
import ErrorModal from 'components/modals/ErrorModal/';
import AmountSelector from 'components/AmountSelector/AmountSelectorMini';
import LinkPreview from 'components/LinkPreview/LinkPreview';
import placeholder from 'assets/images/placeholders/placeholder-image.svg';
import Avatar from 'components/Avatar/Avatar';
import { ReactComponent as EllipsisIcon } from 'assets/images/icons/dots/three-dots-hollow.svg';
import { ReactComponent as GiveIcon } from 'assets/images/icons/svgs/give-bold.svg';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import {
  generateVideoThumbnail,
  getVideoDurationFromFile,
  urlToImageFile,
} from 'util/image';
import { MAX_VIDEO_DURATION_SECONDS } from 'constants/video';
import CauzeSwitch from 'components/CauzeSwitch/CauzeSwitch';
import { CHECKOUT_TYPE } from 'stores/CheckoutStoreV2';
import moment from 'moment';

Quill.register('modules/autoLinks', AutoLinks);

const scrubLink = _.debounce((text, update) => {
  const links = linkify.find(text);
  if (links.length > 0 && links[0].href) {
    update({ previewUrl: links[0].href });
  }
}, 1000);

const modules = {
  autoLinks: true,
  toolbar: [['bold', 'italic', 'underline'], ['link']],
};

const formats = ['bold', 'italic', 'underline', 'link'];

const InputContainer = ({ title, error, className, children, style }) => (
  <div style={style} className={classnames(' flex-column', className)}>
    {title && (
      <p
        className={classnames('input-title flex-column', {
          'input-title-error': error,
        })}
      >
        {title}
      </p>
    )}
    {children}
  </div>
);

const validate = ({
  name,
  charities,
  description,
  startDate,
  endDate,
  hasDates,
  hasMatch,
  matchTotal,
  userMatchLimit,
}) => {
  const validationObj = {
    name: true,
    image: true,
    description: true,
    charities: true,
    dates: true,
    match: true,
    matchType: true,
    matchMultiplier: true,
    matchTotal: true,
    isValid: true,
  };

  if (!name) {
    validationObj.name = false;
    validationObj.isValid = false;
  }
  if (charities.length === 0) {
    validationObj.charities = false;
    validationObj.isValid = false;
  }
  if (hasDates && (!startDate || !endDate)) {
    validationObj.dates = false;
    validationObj.isValid = false;
  }
  if (hasMatch && matchTotal === 0) {
    validationObj.matchTotal = false;
    validationObj.isValid = false;
  }

  // image is always valid because there is a default image
  return validationObj;
};

const CreateCauzeView = ({
  cauzeStore,
  profileStore,
  userContext,
  eventId,
  uiStore,
  cauzeLoaded,
}) => {
  const [isOpenSearch, setIsOpenSearch] = useState(true);
  const [isOpenDonation, setIsOpenDonation] = useState(false);
  const [showImageUploadModal, setShowImageUploadModal] = useState(false);
  const [showImageOrVideoModal, setShowImageOrVideoModal] = useState(false);
  const [showVideoInput, setShowVideoInput] = useState(false);
  const [isChanged, setIsChanged] = useState(false);
  const [showFileError, setShowFileError] = useState(null);
  const [validation, setValidation] = useState({
    name: true,
    image: true,
    description: true,
    charities: true,
    dates: true,
    match: true,
    matchTotal: true,
    isValid: true,
  });
  const [previewImageUrl, setPreviewImageUrl] = useState(
    cauzeStore.currentCauze.previewImageUrl,
  );
  const [matchTotalDisplay, setMatchTotalDisplay] = useState(undefined);
  const [userMatchLimitDisplay, setUserMatchLimitDisplay] = useState(undefined);
  const hasVideo =
    cauzeStore.currentCauze &&
    cauzeStore.currentCauze.previewUrl &&
    (cauzeStore.currentCauze.previewUrl.includes('youtu') ||
      cauzeStore.currentCauze.previewUrl.includes('vimeo'));
  const currentCauze = cauzeStore.currentCauze;

  // Only allow match details to be configured for creating a corporate cauze.
  const showMatchAmounts = userContext.companyId || userContext.userId;
  const matchTotalDifference = cauzeStore.getMatchTotalDifference();
  const checkMatchDetails =
    (currentCauze.hadPreviousMatch && cauzeStore.matchHasChanged) ||
    (!currentCauze.hadPreviousMatch && currentCauze.hasMatch);
  const isGroup = profileStore.getActiveEntity().groupType !== 'COMPANY';
  const isEditing = Boolean(eventId);

  const addFundsRequired = () => {
    return (
      checkMatchDetails &&
      !profileStore.activeEntity.balance.allowNegative &&
      matchTotalDifference > profileStore.activeEntity.balance.total
    );
  };

  // This is my approximation of componentWillReceiveProps comparing props to
  // see if userContext changed. useEffect with a mutable ref to check first run is
  // functionally the same
  const isFirstRun = useRef(true);
  useEffect(() => {
    if (userContext) {
      // .current is a magic useRef thing thats attached to the ref with the default value (true)
      if (isFirstRun.current) {
        isFirstRun.current = false;
      } else {
        cauzeStore.navToCauzes(userContext);
      }
    }

    return () => {
      cauzeStore.onCreateUnmount();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // sets video state when loading in an existing cauze for editing
  useEffect(() => {
    if (hasVideo && !showVideoInput) {
      setShowVideoInput(true);
    }
  }, [hasVideo, showVideoInput]);

  // Auto-set cauze name to first charity name when charities change
  useEffect(() => {
    if (currentCauze.charities && currentCauze.charities.length > 0) {
      const firstCharity = currentCauze.charities[0];
      if (firstCharity && firstCharity.name && !currentCauze.name) {
        cauzeStore.updateCauze({ name: firstCharity.name });
      }
    }
  }, [currentCauze.charities, currentCauze.name, cauzeStore]);

  // Sync display values with currentCauze values only on initial load
  useEffect(() => {
    if (!cauzeLoaded) return;

    if (matchTotalDisplay === undefined) {
      setMatchTotalDisplay(
        currentCauze.matchTotal === 0
          ? ''
          : `$${(currentCauze.matchTotal / 100).toFixed(2)}`,
      );
    }

    if (userMatchLimitDisplay === undefined) {
      setUserMatchLimitDisplay(
        currentCauze.userMatchLimit === 0
          ? ''
          : `$${(currentCauze.userMatchLimit / 100).toFixed(2)}`,
      );
    }
  }, [cauzeLoaded]);

  const handleSubmit = (event, draft = false) => {
    event.preventDefault();

    const validationResult = validate({
      ...cauzeStore.currentCauze,
    });

    const _handleSubmit = async () => {
      if (isEditing) {
        if (!currentCauze.hasMatch && currentCauze.hadPreviousMatch) {
          await cauzeStore.deleteCauzeMatch({ eventId: parseInt(eventId) });
        }

        await cauzeStore.updatePlannedEvent({
          eventState: draft ? 'UNPUBLISHED' : 'ACTIVE',
          eventId,
          userContext,
        });
      } else {
        await cauzeStore.createPlannedEvent({
          userContext,
          eventState: draft ? 'UNPUBLISHED' : 'ACTIVE',
        });
      }
    };

    if (validationResult.isValid) {
      if (addFundsRequired()) {
        uiStore.openModal('CHECKOUT', {
          checkoutType: CHECKOUT_TYPE.GIFT,
          giftType: 'SELF_GIFT',
          amount:
            matchTotalDifference - profileStore.activeEntity.balance.total,
          onCheckoutSuccess: async (state) => {
            if (state.giftType === 'SELF_GIFT') {
              uiStore.showNotification({
                body: `$${state.amount / 100} was added to your account`,
                type: 'SUCCESS',
              });
            } else {
              uiStore.showNotification({
                body: `${state.amount === 0 ? 'Invites' : 'Gifts'} Sent`,
                type: 'SUCCESS',
              });
            }

            await profileStore.getProfile();
            await _handleSubmit();
          },
        });

        return;
      }

      _handleSubmit();
    } else {
      window.scrollTo(0, 0);
    }

    setValidation(validationResult);
  };

  const toggleDropdown = () => setIsOpenSearch((prev) => !prev);

  const getErrorMessage = () => {
    if (!validation.charities) {
      return 'Please select at least one nonprofit';
    }
    if (!validation.matchTotal && currentCauze.hasMatch) {
      return 'Please enter a match total';
    }
    if (!validation.dates && currentCauze.hasDates) {
      return 'Please select an end date';
    }
    return '';
  };

  return (
    <div className="create-cauze flex-column">
      <ErrorModal
        onToggleClose={cauzeStore.resetImage}
        errorText="There was an issue uploading the image. Support has been notified."
        isOpen={cauzeStore.imageUploadError}
      />
      <ErrorModal
        onToggleClose={cauzeStore.clearCauzeErrors}
        errorText="There was an issue publishing your cauze. Support has been notified."
        isOpen={cauzeStore.createCauzeError}
      />
      <ImageUploadModal
        updateImageUrl={(previewImageUrl) => {
          cauzeStore.updateCauze({
            previewImageUrl,
            externalImageUrl: null,
            previewUrl: null,
          });
        }}
        onSave={() => setShowImageOrVideoModal(false)}
        store={cauzeStore}
        isActive={showImageUploadModal}
        onToggleClose={() => setShowImageUploadModal(false)}
        src={placeholder}
        isChanged={isChanged}
        setIsChanged={setIsChanged}
        showFileError={showFileError}
        setShowFileError={setShowFileError}
        upImg={previewImageUrl}
        setUpImg={setPreviewImageUrl}
      />

      <div>
        <section className="container">
          <div className="text-center font-agenda-bold max-sm:mb-4 ">
            <h1 className="text-[24px] leading-[29px] uppercase">
              {isEditing ? 'Edit your Cauze' : 'START A CAUZE'}
            </h1>
          </div>
          {cauzeStore.loadingCauze ? (
            <CauzeSpinner />
          ) : (
            <div className="p-5 max-sm:p-0">
              <form onSubmit={handleSubmit} className="gap-5">
                <div className="flex flex-col gap-y-2.5">
                  <label className="font-agenda text-[16px] h-12 sm:h-[19px]">
                    Title (Optional)
                  </label>

                  <div>
                    <input
                      type="text"
                      name="name"
                      value={currentCauze.name}
                      placeholder="Cauze Title"
                      className="border border-solid border-lightgray-c4c w-full rounded-[8px] h-[45px] pl-4"
                      onChange={(e) => {
                        cauzeStore.updateCauze({
                          name: e.target.value,
                        });
                      }}
                    />
                  </div>
                </div>

                <div
                  className="flex justify-between items-center cursor-pointer"
                  onClick={toggleDropdown}
                >
                  <div className="flex gap-2.5 items-center">
                    <NonProfitIcon className="" />
                    <div className="font-agenda flex justify-center items-center text-[16px] translate-y-[1px]">
                      Non-Profits
                    </div>
                  </div>
                  <div className="flex gap-2.5 items-center justify-center ">
                    {cauzeStore.currentCauze &&
                    cauzeStore.currentCauze.charities &&
                    cauzeStore.currentCauze.charities.length > 0 ? (
                      <>
                        {' '}
                        <div className="event-item-avatar-group">
                          {cauzeStore?.currentCauze?.charities
                            ?.slice(0, 5)
                            .map((charity) => (
                              <Avatar
                                xsm
                                noBorder
                                avatar={charity.avatar}
                                avatarUrls={charity.avatarUrls}
                                entityType={'CHARITY'}
                              />
                            ))}
                          {cauzeStore?.currentCauze?.charities >= 5 && (
                            <EllipsisIcon className="event-item-ellipsis" />
                          )}
                        </div>{' '}
                      </>
                    ) : (
                      <div className="translate-y-[1px] text-[14px] flex items-center font-agenda-bold">
                        Select
                      </div>
                    )}
                    <RightArrowIcon
                      className={`transition-transform duration-300 ${
                        isOpenSearch ? 'rotate-90' : ''
                      }`}
                    />
                  </div>
                </div>

                <div className={` ${isOpenSearch ? 'block' : 'hidden'}`}>
                  <InputContainer
                    title="Choose Nonprofit(s) *"
                    error={!validation.charities}
                  >
                    <CharitySearch
                      defaultValue={toJS(currentCauze.charities)}
                      uiStore={uiStore}
                      showSelectionList
                      onChange={(charities) => {
                        setValidation({
                          ...validation,
                          charities: true,
                          isValid: true,
                        });
                        cauzeStore.updateCauze({
                          charities,
                        });
                      }}
                    />
                    <div className="flex items-center gap-1 text-gray-500 text-sm mt-1">
                      <FontAwesomeIcon icon={faInfoCircle} />
                      <span className="text-xs">
                        A Cauze can support one or more nonprofits
                      </span>
                    </div>
                  </InputContainer>
                </div>

                {!isEditing && false && (
                  <>
                    <div
                      className="flex justify-between items-center cursor-pointer"
                      onClick={() => setIsOpenDonation(!isOpenDonation)}
                    >
                      <div className="flex gap-2.5 items-center">
                        <GiveIcon height={20} width={20} />
                        <div className="font-agenda flex justify-center items-center text-[16px] translate-y-[1px]">
                          Your Donation
                        </div>
                      </div>
                      <div className="flex gap-2.5 items-center justify-center ">
                        <div className="translate-y-[1px] text-[14px] flex items-center font-agenda-bold">
                          Select
                        </div>
                        <RightArrowIcon
                          className={`transition-transform duration-300 ${
                            isOpenSearch ? 'rotate-90' : ''
                          }`}
                        />
                      </div>
                    </div>

                    <div className={`${isOpenDonation ? 'block' : 'hidden'}`}>
                      <InputContainer
                        title="Choose Donation Amount"
                        error={!validation.charities}
                      >
                        <div className="flex flex-1 w-full">
                          <AmountSelector
                            amounts={[1000, 2500, 5000, 10000]}
                            defaultAmount={5000}
                            sendDefaultUpdate={false}
                            onAmountUpdate={() => {}}
                          />
                        </div>
                      </InputContainer>
                    </div>
                  </>
                )}

                <div className="flex max-sm:flex-col gap-4 py-2.5  border-y-[1px] border-x-0 border-[#ddd] border-solid">
                  {showVideoInput && (
                    <div className="flex-column ">
                      <input
                        type="text"
                        name="video"
                        style={{
                          border: '1px solid hsl(0,0%,80%)',
                          borderRadius: '4px',
                          margin: '0.5rem',
                        }}
                        value={currentCauze.previewUrl}
                        placeholder={'Youtube or Vimeo Video Link'}
                        onChange={(e) => {
                          scrubLink(e.target.value, cauzeStore.updateCauze);
                          cauzeStore.updateCauze({
                            previewUrl: e.target.value,
                          });
                        }}
                      />
                      {hasVideo && (
                        <LinkPreview
                          style={{ margin: '0.5rem' }}
                          smallPreview
                          uri={currentCauze.previewUrl}
                          onRemovePreview={() =>
                            cauzeStore.updateCauze({ previewUrl: '' })
                          }
                          onPreviewLoaded={({ previewImageUrl }) =>
                            cauzeStore.updateCauze({
                              externalImageUrl: previewImageUrl,
                            })
                          }
                        />
                      )}
                    </div>
                  )}

                  {!showVideoInput && (
                    <div
                      className="column flex-column flex-center h-[224px] min-w-[224px] self-start !p-0"
                      style={{ position: 'relative' }}
                    >
                      <img
                        className="h-full w-full p-0 object-contain"
                        onClick={() => setShowImageOrVideoModal(true)}
                        style={{ cursor: 'pointer' }}
                        src={
                          previewImageUrl ||
                          currentCauze.previewImageUrl ||
                          placeholder
                        }
                      />
                      {(previewImageUrl || currentCauze.previewImageUrl) && (
                        <PencilIcon
                          onClick={() => setShowImageOrVideoModal(true)}
                          className="absolute top-2 left-2"
                        />
                      )}
                      {cauzeStore.isUploadingCauzeImage && (
                        <CauzeSpinner
                          size={'50px'}
                          style={{
                            position: 'absolute',
                            top: 'calc(50% - 25px)',
                            left: 0,
                            right: 0,
                          }}
                        />
                      )}
                    </div>
                  )}
                  <CauzeImageOrVideo
                    currentCauze={currentCauze}
                    cauzeStore={cauzeStore}
                    hasVideo={hasVideo}
                    validation={validation}
                    setShowVideoInput={setShowVideoInput}
                    showVideoInput={showVideoInput}
                    setShowImageUploadModal={setShowImageUploadModal}
                    store={cauzeStore}
                    onToggleClose={() => setShowImageOrVideoModal(false)}
                    isActive={showImageOrVideoModal}
                    setIsChanged={setIsChanged}
                    setShowFileError={setShowFileError}
                    uiStore={uiStore}
                    previewImageUrl={previewImageUrl}
                    setPreviewImageUrl={setPreviewImageUrl}
                  />
                  <InputContainer
                    className="self-center w-full"
                    error={!validation.description}
                  >
                    <ReactQuill
                      formats={formats}
                      modules={modules}
                      theme="snow"
                      defaultValue={currentCauze.description || ''}
                      placeholder="Why are you giving?"
                      onChange={(description) => {
                        setValidation({
                          ...validation,
                          description: true,
                          isValid: true,
                        });
                        cauzeStore.updateCauze({ description });
                      }}
                    />
                  </InputContainer>
                </div>

                <h3 className="font-agenda-bold text-[20px] leading-[20px]">
                  Optional Settings
                </h3>

                <div className="grid grid-cols-2 gap-x-28">
                  <h3 className="font-agenda-bold text-[18px] leading-[18px] h-[18px] my-auto">
                    End Date
                  </h3>
                  <CauzeSwitch
                    isSelected={currentCauze.hasDates}
                    onChange={() => {
                      cauzeStore.updateCauze({
                        hasDates: !currentCauze.hasDates,
                        startDate: !currentCauze.hasDates
                          ? moment().hours(0).minutes(0).seconds(0)
                          : null,
                        endDate: !currentCauze.hasDates
                          ? moment()
                              .add(30, 'days')
                              .hours(0)
                              .minutes(0)
                              .seconds(0)
                          : null,
                      });
                    }}
                  />
                </div>

                {currentCauze.hasDates && (
                  <InputContainer
                    title="How long do you want to run your Cauze?"
                    error={!validation.dates}
                  >
                    <div>
                      <label
                        className="font-agenda text-[16px] h-6"
                        htmlFor="no-dates"
                      >
                        <input
                          onChange={(_ev) => {
                            cauzeStore.toggleHasDates();
                          }}
                          type="radio"
                          id="no-dates"
                          checked={!currentCauze.hasDates}
                          name="no-dates"
                          value="no-dates"
                        />
                        No End Date
                      </label>
                    </div>
                    <div>
                      <label
                        className="font-agenda text-[16px] h-6"
                        htmlFor="show-dates"
                      >
                        <input
                          onChange={(_ev) => {
                            cauzeStore.toggleHasDates();
                          }}
                          type="radio"
                          id="show-dates"
                          name="show-dates"
                          value="show-dates"
                          checked={currentCauze.hasDates}
                        />
                        {currentCauze.hasDates
                          ? 'Run Cauze'
                          : 'Run Cauze For...'}
                      </label>
                      {currentCauze.hasDates && (
                        <DatePicker
                          startDate={currentCauze.startDate}
                          endDate={currentCauze.endDate}
                          onDatesChange={cauzeStore.updateCauze}
                          noBorder
                        />
                      )}
                    </div>
                  </InputContainer>
                )}

                <div className="grid grid-cols-2 gap-x-28">
                  <h3 className="font-agenda-bold text-[18px] leading-[18px] h-[18px] my-auto">
                    Match Donations
                  </h3>
                  <CauzeSwitch
                    isSelected={currentCauze.hasMatch}
                    onChange={() => {
                      cauzeStore.updateCauze({
                        hasMatch: !currentCauze.hasMatch,
                      });
                    }}
                  />
                </div>

                {showMatchAmounts && currentCauze.hasMatch && (
                  <>
                    <InputContainer title="Who do you want to match?">
                      <div>
                        <div className="flex-column font-agenda text-[16px] leading-[120%]">
                          <label htmlFor="all-users" className="h-6">
                            <input
                              onChange={(_ev) => {
                                cauzeStore.updateCauze({
                                  matchType: 'EVENT',
                                });
                              }}
                              type="radio"
                              id="all-users"
                              name="all-users"
                              value="all-users"
                              checked={currentCauze.matchType === 'EVENT'}
                            />
                            Match any Cauze user
                          </label>
                          <label htmlFor="employee-only" className="h-6">
                            <input
                              onChange={(_ev) => {
                                cauzeStore.updateCauze({
                                  matchType: 'COMPANY',
                                });
                              }}
                              type="radio"
                              id="employee-only"
                              name="employee-only"
                              value="employee-only"
                              checked={currentCauze.matchType === 'COMPANY'}
                            />
                            Only match my {isGroup ? 'members' : 'employees'}
                          </label>
                          <label htmlFor="follower-only" className="h-6">
                            <input
                              onChange={(_ev) => {
                                cauzeStore.updateCauze({
                                  matchType: 'FOLLOWER',
                                });
                              }}
                              type="radio"
                              id="follower-only"
                              name="follower-only"
                              value="follower-only"
                              checked={currentCauze.matchType === 'FOLLOWER'}
                            />
                            Only match my followers
                          </label>
                        </div>
                      </div>
                    </InputContainer>

                    <InputContainer title="Match Multiplier">
                      <div>
                        <div>
                          <div className="flex-column font-agenda text-[16px] leading-[120%]">
                            <label
                              htmlFor="1x-multiplier"
                              className="h-12 sm:h-6"
                            >
                              <input
                                onChange={(_ev) => {
                                  cauzeStore.setMatchHasChanged(true);
                                  cauzeStore.updateCauze({
                                    matchMultiplier: 1,
                                  });
                                }}
                                type="radio"
                                id="1x-multiplier"
                                name="1x-multiplier"
                                value={1}
                                checked={currentCauze.matchMultiplier === 1}
                              />
                              1x Match ($1 for each $1 donated by the giver)
                            </label>
                            <label
                              htmlFor="2x-multiplier"
                              className="h-12 sm:h-6"
                            >
                              <input
                                onChange={(_ev) => {
                                  cauzeStore.setMatchHasChanged(true);
                                  cauzeStore.updateCauze({
                                    matchMultiplier: 2,
                                  });
                                }}
                                type="radio"
                                id="2x-multiplier"
                                name="2x-multiplier"
                                value={2}
                                checked={currentCauze.matchMultiplier === 2}
                              />
                              2x Match ($2 for each $1 donated by the giver)
                            </label>
                            <label
                              htmlFor="3x-multiplier"
                              className="h-12 sm:h-6"
                            >
                              <input
                                onChange={(_ev) => {
                                  cauzeStore.setMatchHasChanged(true);
                                  cauzeStore.updateCauze({
                                    matchMultiplier: 3,
                                  });
                                }}
                                type="radio"
                                id="3x-multiplier"
                                name="3x-multiplier"
                                value={3}
                                checked={currentCauze.matchMultiplier === 3}
                              />
                              3x Match ($3 for each $1 donated by the giver)
                            </label>
                          </div>
                        </div>
                      </div>
                    </InputContainer>

                    <InputContainer
                      title="Maximum Match"
                      error={!validation.matchTotal}
                    >
                      <div className="flex flex-col gap-y-[18px]">
                        <div className="flex flex-col gap-y-2.5">
                          <label className="font-agenda text-[16px] h-[19px]">
                            How much do you want to match total? *
                          </label>

                          <div>
                            <input
                              type="text"
                              name="matchTotal"
                              value={matchTotalDisplay}
                              placeholder="$0.00"
                              onChange={(e) => {
                                const inputValue = e.target.value;
                                setMatchTotalDisplay(inputValue);

                                // Only update store if we have a valid number
                                const cleanValue = inputValue.replace(
                                  /[$,]/g,
                                  '',
                                );
                                const numValue = parseFloat(cleanValue);

                                if (!isNaN(numValue) || cleanValue === '') {
                                  cauzeStore.setMatchHasChanged(true);
                                  setValidation({
                                    ...validation,
                                    matchTotal: true,
                                  });
                                  cauzeStore.updateCauze({
                                    matchTotal: numValue * 100 || 0,
                                  });
                                }
                              }}
                              onBlur={() => {
                                // Format on blur
                                const cleanValue = matchTotalDisplay.replace(
                                  /[$,]/g,
                                  '',
                                );
                                const numValue = parseFloat(cleanValue);
                                if (!isNaN(numValue) && numValue > 0) {
                                  setMatchTotalDisplay(
                                    `$${numValue.toFixed(2)}`,
                                  );
                                } else {
                                  setMatchTotalDisplay('');
                                }
                              }}
                              className="border border-solid border-lightgray-c4c w-full rounded-[8px] h-[45px] pl-4"
                            />
                          </div>

                          {!validation.matchTotal && (
                            <p className="font-agenda text-[14px] text-red-500">
                              Please enter a valid match total
                            </p>
                          )}

                          {addFundsRequired() && (
                            <p className="font-agenda text-[14px] text-red-500">
                              Match exceeds your balance. Funds will be added in
                              the next step.
                            </p>
                          )}
                        </div>

                        <div className="flex flex-col gap-y-2.5">
                          <label className="font-agenda text-[16px] h-12 sm:h-[19px]">
                            What is the most you'll match from any one giver?
                            (Optional)
                          </label>

                          <div>
                            <input
                              type="text"
                              name="userMatchLimit"
                              value={userMatchLimitDisplay}
                              placeholder="$0.00"
                              onChange={(e) => {
                                const inputValue = e.target.value;
                                setUserMatchLimitDisplay(inputValue);

                                // Only update store if we have a valid number
                                const cleanValue = inputValue.replace(
                                  /[$,]/g,
                                  '',
                                );
                                const numValue = parseFloat(cleanValue);

                                if (!isNaN(numValue) || cleanValue === '') {
                                  cauzeStore.setMatchHasChanged(true);
                                  cauzeStore.updateCauze({
                                    userMatchLimit: numValue * 100 || 0,
                                  });
                                }
                              }}
                              onBlur={() => {
                                // Format on blur
                                const cleanValue =
                                  userMatchLimitDisplay.replace(/[$,]/g, '');
                                const numValue = parseFloat(cleanValue);
                                if (!isNaN(numValue) && numValue > 0) {
                                  setUserMatchLimitDisplay(
                                    `$${numValue.toFixed(2)}`,
                                  );
                                } else {
                                  setUserMatchLimitDisplay('');
                                }
                              }}
                              className="border border-solid border-lightgray-c4c w-full rounded-[8px] h-[45px] pl-4"
                            />
                          </div>
                        </div>
                      </div>
                    </InputContainer>
                  </>
                )}

                <div className="mt-5">
                  {getErrorMessage() && (
                    <div className="bg-red-50 text-red-600 p-4 rounded">
                      <p className="font-agenda-medium text-[14px] leading-[14px] text-center">
                        {getErrorMessage()}
                      </p>
                    </div>
                  )}

                  <div className="flex-row items-center w-full mx-2.5 font-agenda-bold">
                    {currentCauze.eventState === 'UNPUBLISHED' && (
                      <Button
                        onClick={(ev) => handleSubmit(ev, true)}
                        style={{ marginRight: '1rem' }}
                        white
                        className="create-button hover:bg-[#eee] !font-agenda-bold"
                        isSubmitting={cauzeStore.savingCauze}
                        disabled={cauzeStore.savingCauze}
                      >
                        Save as Draft
                      </Button>
                    )}
                    <Button
                      className="create-button !border-cauzeorange !font-agenda-bold disabled:!bg-lightgray-b2b disabled:!border-lightgray-b2b"
                      type="submit"
                      isSubmitting={cauzeStore.savingCauze}
                      disabled={cauzeStore.savingCauze}
                    >
                      {addFundsRequired() ? (
                        'Add Funds'
                      ) : (
                        <>{!isEditing && `Save & `}Publish</>
                      )}
                    </Button>
                  </div>
                </div>
              </form>
            </div>
          )}
          <div className="dashboard flex-row flex-space-around"></div>
        </section>
      </div>
    </div>
  );
};

export default inject(
  'cauzeStore',
  'profileStore',
  'uiStore',
)(observer(CreateCauzeView));

const CauzeImageOrVideo = ({
  cauzeStore,
  currentCauze,
  validation,
  setShowImageUploadModal,
  hasVideo,
  showVideoInput,
  setShowVideoInput,
  onToggleClose,
  isActive,
  setIsChanged,
  setShowFileError,
  uiStore,
  previewImageUrl,
  setPreviewImageUrl,
}) => {
  const inputRef = useRef(null);
  const [showUploadContent, setShowUploadContent] = useState(false);

  const onSelectFile = async (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const targetFile = e.target.files[0];

      if (targetFile.type.includes('video')) {
        const duration = await getVideoDurationFromFile(targetFile);

        if (duration > MAX_VIDEO_DURATION_SECONDS) {
          uiStore.showNotification({
            body: 'Video uploads must be under 1 minute.',
            type: 'ERROR',
          });
          return;
        }

        cauzeStore.setLoading(true);
        generateVideoThumbnail(targetFile, async ({ image, width, height }) => {
          cauzeStore.updateCauze({
            previewImageUrl: image,
            externalImageUrl: null,
            previewUrl: null,
          });

          setPreviewImageUrl(image);

          await Promise.all([
            urlToImageFile(image).then((imgFile) =>
              cauzeStore.uploadImage(imgFile),
            ),

            cauzeStore.startVideoUpload({
              width,
              height,
              video: targetFile,
            }),
          ]);

          cauzeStore.setLoading(false);
          onToggleClose();
        });
      } else if (e.target.files[0].size > 3000000) {
        setShowFileError(
          'File is too large. Please select a file less than 3MB.',
        );
      } else {
        setIsChanged(true);
        setShowImageUploadModal(true);
        const reader = new FileReader();
        reader.addEventListener('load', () =>
          setPreviewImageUrl(reader.result),
        );
        reader.readAsDataURL(e.target.files[0]);
      }
    }
  };

  return (
    <div
      className={classnames('modal image-upload-modal !z-30', {
        'is-active': isActive,
      })}
    >
      <div onClick={onToggleClose} className="modal-background"></div>
      <div style={{ backgroundColor: 'white' }} className="modal-card">
        <header className="modal-card-head flex-center">
          <button
            className="delete"
            type="button"
            aria-label="close"
            onClick={onToggleClose}
          ></button>
          <div className="flex-column flex-center">
            <div className="text-center text-bold">Cauze Image or Video</div>
          </div>
        </header>
        <section className="modal-card-body">
          <InputContainer error={!validation.image}>
            <div className="flex max-sm:flex-col">
              <div className="column">
                <div>
                  <input
                    onChange={(_ev) => {
                      cauzeStore.updateCauze({
                        previewImageUrl: '',
                        imageId: null,
                        externalImageUrl: null,
                        previewUrl: null,
                      });
                      setPreviewImageUrl('');
                      setShowVideoInput(false);
                      setShowUploadContent(false);
                    }}
                    type="radio"
                    id="default-image"
                    checked={
                      currentCauze.previewImageUrl === '' &&
                      !showUploadContent &&
                      !showVideoInput
                    }
                    name="cauze-image"
                    value="default-image"
                  />
                  <label htmlFor="default-image">Use default image</label>
                </div>
                <div>
                  <input
                    onChange={(_ev) => {
                      setShowUploadContent(true);
                    }}
                    type="radio"
                    id="upload-image"
                    name="cauze-image"
                    value="upload-image"
                    checked={showUploadContent && !showVideoInput}
                  />
                  <label htmlFor="upload-image">Upload an Image/Video</label>
                </div>
                <div>
                  <input
                    onChange={(_ev) => setShowVideoInput(true)}
                    type="radio"
                    id="default-image"
                    checked={showVideoInput}
                    name="cauze-image"
                    value="default-image"
                  />
                  <label htmlFor="default-image">YouTube/Vimeo Link</label>
                </div>
                {showVideoInput && (
                  <div className="flex-column">
                    <input
                      type="text"
                      name="video"
                      style={{
                        border: '1px solid hsl(0,0%,80%)',
                        borderRadius: '4px',
                        margin: '0.5rem',
                      }}
                      value={previewImageUrl}
                      placeholder={'Youtube or Vimeo Video Link'}
                      onChange={(e) => {
                        scrubLink(e.target.value, cauzeStore.updateCauze);
                        cauzeStore.updateCauze({
                          previewUrl: e.target.value,
                        });
                      }}
                    />
                    {hasVideo && (
                      <LinkPreview
                        style={{ margin: '0.5rem' }}
                        smallPreview
                        uri={currentCauze.previewUrl}
                        onRemovePreview={() =>
                          cauzeStore.updateCauze({ previewUrl: '' })
                        }
                        onPreviewLoaded={({ previewImageUrl }) =>
                          cauzeStore.updateCauze({
                            externalImageUrl: previewImageUrl,
                          })
                        }
                      />
                    )}
                  </div>
                )}
              </div>

              <input
                hidden
                type="file"
                id="hero-upload"
                onChange={onSelectFile}
                ref={inputRef}
              />

              {!showVideoInput && showUploadContent && (
                <div
                  className="column flex-column flex-center"
                  style={{ position: 'relative' }}
                >
                  <img
                    onClick={() => inputRef.current.click()}
                    style={{ cursor: 'pointer' }}
                    src={previewImageUrl || placeholder}
                  />
                  {cauzeStore.isUploadingCauzeImage && (
                    <CauzeSpinner
                      size={'50px'}
                      style={{
                        position: 'absolute',
                        top: 'calc(50% - 25px)',
                        left: 0,
                        right: 0,
                      }}
                    />
                  )}
                  <a
                    onClick={(ev) => {
                      ev.preventDefault();
                      inputRef.current.click();
                    }}
                  >
                    View/Edit Image
                  </a>
                </div>
              )}
            </div>
          </InputContainer>
        </section>
      </div>
    </div>
  );
};
